import { API } from '@/modules/api'
import type { TIncidentIqFeeCartLine } from '@/types/cart'
import { defineStore } from 'pinia'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'
import type { IncidentIqFee } from '@/modules/api/get/incidentIqFees.ts'
import { useCartStore } from '@/stores/cart'
import { hasUnsignedForms } from '@/mixins/OtherFeeHelper'

export const createUniqueId = (feeId: string) => feeId + '::' + OrderItemTypeCode.IncidentIqFee

export const useIncidentIqStore = defineStore('incidentIq', {
  state: () => ({
    isLoaded: {} as Record<TStudentAccountId, boolean>,
    fees: {} as Record<TStudentAccountId, IncidentIqFee[]>
  }),

  getters: {
    isLoading(state) {
      return (studentAccountId: TStudentAccountId) => !(state.isLoaded[studentAccountId] ?? false)
    },

    getFees(state) {
      return (studentAccountId: TStudentAccountId) => state.fees[studentAccountId] ?? []
    }
  },

  actions: {
    async fetch(_districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      this.isLoaded[studentAccountId] = false
      this.fees[studentAccountId] = await API.Get.incidentIqFees(studentAccountId)
      this.validateCart(_districtId, studentAccountId)
      this.isLoaded[studentAccountId] = true
    },

    // Incident IQ fees are read live from IIQ on every load and have no local row
    // until they are paid, so a fee can disappear or shrink between loads (paid or
    // voided on IIQ's side). Drop or clamp any cart line that no longer matches,
    // so checkout can't be attempted against a stale amount.
    validateCart(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      const cartStore = useCartStore()
      for (const line of cartStore.getCartLines(districtId) as TIncidentIqFeeCartLine[]) {
        if (
          line.orderItemType === OrderItemType.IncidentIqFee &&
          line.studentAccountId === studentAccountId
        ) {
          const fee = this.fees[studentAccountId].find(fee => fee.feeId === line.feeId)
          if (!fee) {
            this.removeFromCart(districtId, line.feeId)
            continue
          }
          // DTA-659: the cart is persisted and rehydrated verbatim, so the
          // add-time form gate never runs again on a restored line. A line added
          // before the item required a form otherwise sits in the cart and pays
          // the charge with no submission — and for Incident IQ there is no
          // server-side gate behind this one.
          if (hasUnsignedForms(fee)) {
            this.removeFromCart(districtId, line.feeId)
            continue
          }
          if (fee.amountDue < line.amount) {
            cartStore.updateCartLine(districtId, line.uniqueId, {
              ...line,
              amount: fee.amountDue
            })
          }
        }
      }
    },

    addToCart(
      districtId: TDistrictId,
      feeData: Omit<TIncidentIqFeeCartLine, 'orderItemType' | 'uniqueId'>
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId)
      const existingLine = cartStore.getCartLine(districtId, uniqueId)

      if (existingLine) {
        cartStore.updateCartLine(districtId, uniqueId, feeData)
      } else {
        cartStore.addCartLine(districtId, uniqueId, {
          ...feeData,
          orderItemType: OrderItemType.IncidentIqFee,
          uniqueId
        })
      }
    },

    updateCartLine(
      districtId: TDistrictId,
      feeData: Partial<TIncidentIqFeeCartLine> & { feeId: string }
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId)
      cartStore.updateCartLine(districtId, uniqueId, feeData)
    },

    removeFromCart(districtId: TDistrictId, feeId: string) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeId)
      cartStore.removeCartLine(districtId, uniqueId)
    }
  }
})
